import { cn } from '@/utils'
import { Camera } from '@react-three/fiber'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ScreenshotList } from './ScreenshotList'
import { ScreenshotsFilterDropdown } from './ScreenshotsFilterDropdown'

import { useDeleteView } from '@/services/queries/views'
import { useCADQuery } from '@/services/queries/cads'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCadPageParams } from '@/pages/CADPage/hooks'
import { useScreenshots } from './hooks/useScreenshots'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useSetCadFromView } from './hooks/useSetCadFromView'
import { useCADPageStore } from '@/state'
import { useIsReadOnly } from '../../hooks/useIsReadOnly'
import { formatNumber } from '@/lib/formatNumber'
import { Spinner } from '@/components/ui/spinner'

import type { DocumentTypeChoices } from '@/lib/api/client'

export const ScreenshotPanelButton = ({ camera }: { camera: Camera }) => {
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const isPanelOpen = useCADPageStore(
    useShallow((state) => state.screenshotPanelOpen),
  )
  const isRetakeModeActive = useCADPageStore(
    (state) => state.isRetakeModeActive,
  )
  const setIsPanelOpen = (open: boolean) =>
    setCadPageState({ screenshotPanelOpen: open })

  const { isLoading: isDocLoading, data } = useDocumentPageQuery()

  const { documentId, dv: selectedDocumentVersionId } = useCadPageParams()

  const { isLoading: isCadLoading, data: cadData } = useCADQuery()

  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const { isLoading: isReadOnlyInfoLoading, data: readOnlyInfo } =
    useIsReadOnly()

  const documentType = data?.documentType as DocumentTypeChoices
  const cadVersionId = cadData?.version?.id
  const cadId = cadData.cad?.id
  const gltfScene = cadData.gltf?.scene

  const { isLoading: isLoadingScreenshots, data: views } = useScreenshots({
    documentId,
    cadVersionId,
    documentVersionId: selectedDocumentVersionId,
  })

  const { mutate: deleteView } = useDeleteView()

  const setCadFromView = useSetCadFromView({ camera })

  const isLoading =
    isLoadingSteps ||
    isLoadingScreenshots ||
    isCadLoading ||
    isReadOnlyInfoLoading ||
    isDocLoading

  const isReadOnly = Boolean(readOnlyInfo?.isReadOnly)

  if (!gltfScene || !cadId || isCadLoading) {
    return null
  }

  return (
    <div className="absolute transform-none w-44 h-36 bg-transparent !z-20 !top-5 !right-0 !left-auto select-none">
      <Sheet open={isPanelOpen} onOpenChange={setIsPanelOpen}>
        <SheetTrigger asChild disabled={isLoading}>
          <Button
            id="screenshot-button"
            data-testid="screenshot-button"
            className={cn('rounded-full', {
              'opacity-40 pointer-events-none': isRetakeModeActive,
            })}
            onMouseDown={() => {
              setCadPageState({
                filterScreenshotsByDocumentPageId: null,
              })
            }}
          >
            <div className="relative">
              <span>Screenshots</span>
              {views?.length !== 0 && (
                <span
                  className="absolute bottom-4 rounded-full px-1.5 py-0"
                  style={{
                    fontSize: 10,
                    backgroundColor: '#ff7f50',
                  }}
                >
                  {isLoading ? (
                    <div className="py-1">
                      <Spinner className="w-3 h-3" />
                    </div>
                  ) : (
                    formatNumber(views?.length || 0)
                  )}
                </span>
              )}
            </div>
          </Button>
        </SheetTrigger>
        <SheetContent data-testid="screenshot-panel" className="max-w-3xl">
          <SheetHeader>
            <SheetTitle>Screenshots</SheetTitle>
          </SheetHeader>
          <div className="h-full pb-12">
            <div className="flex justify-start my-6">
              <ScreenshotsFilterDropdown />
            </div>

            <ScreenshotList
              views={views || []}
              steps={steps}
              isLoading={isLoading}
              isReadOnly={isReadOnly}
              onRetake={async (view) => {
                if (!view.id) return
                await setCadFromView(view)
                setCadPageState({
                  retakeViewId: view.id,
                  isRetakeModeActive: true,
                  screenshotPanelOpen: false,
                  retakeLocation: 'screenshotPanel',
                })
              }}
              onDelete={(viewId) => {
                if (!viewId) return
                deleteView({ viewId, documentType })
              }}
              onClick={async ({ view, attachedStep }) => {
                await setCadFromView(view)
                if (attachedStep) {
                  setCadPageState({
                    screenshotPanelOpen: false,
                    subAssemblyDocumentPageId: attachedStep.parent,
                  })
                }
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
